<template>
    <div class="py-2">
        <VueGoodTable
            :columns="columns"
            :rows="rows"
            :sort-options="{
                enabled: false,
                initialSortBy: {field: 'property', type: 'asc'},
            }"
            :search-options="{
                enabled: true,
                placeholder: $t('Search'),
            }"
            :group-options="{
                enabled: true,
                collapsable: true,
            }"
            @on-row-click="clickRow"
        >
            <div slot="emptystate" class="flex items-center justify-center text-gray-500 py-1">
                {{ $t('No contacts found.') }}
            </div>

            <template slot="table-header-row" slot-scope="props">
                <span class="w-full flex items-center justify-between">
                    <div class="flex items-center space-x-4">
                        <span class="text-lg pl-1">
                            {{ $t(props.row.label.name) }}
                        </span>

                        <a v-if="props.row.label.email"
                            :href="'mailto:' + props.row.label.email"
                            class="group font-normal text-sm text-gray-700 hover:text-blue-600 pt-0.5"
                        >
                            <i class="fa fa-envelope ml-1 mr-1 text-xs text-gray-500 group-hover:text-blue-500"></i>
                            {{ $t(props.row.label.email) }}
                        </a>

                        <span v-if="props.row.label.address"
                            class="font-normal text-sm text-gray-700 pt-0.5 px-2"
                        >
                            <span class="-ml-2 mr-2 text-gray-500">/</span>

                            <span class="ml-0.5">
                                {{ $t(props.row.label.address) }}
                            </span>
                        </span>

                        <span v-if="props.row.label.phone"
                            class="font-normal text-sm text-gray-700 pt-0.5 px-2"
                        >
                            <span class="-ml-2 mr-2 text-gray-500">/</span>

                            <span class="ml-0.5">
                                <i class="fa fa-phone-alt ml-1 mr-1 text-xs text-gray-500"></i>
                                {{ $t(props.row.label.phone) }}
                            </span>
                        </span>
                    </div>

                    <div class="flex items-center space-x-4">
                        <a class="text-sm font-normal text-gray-700 hover:text-blue-500 pt-0.5">
                            <i class="fa fa-home mr-1 text-xs text-gray-500"></i>
                            {{ $t(props.row.label.deal_count) }} {{ $t(props.row.label.deal_count) === 1 ? $t('property') : $t('properties') }}
                        </a>
                    </div>
                </span>
            </template>

            <template slot="table-row" slot-scope="props">
                <span v-if="props.column.field == 'property'">
                    <span class="pl-1">
                        <i class="fa fa-home mr-2 text-sm text-gray-400"></i>

                        {{ $t(props.row.property) }}
                    </span>
                </span>

                <span v-else>
                    {{ $t(props.formattedRow[props.column.field]) }}
                </span>
            </template>
        </VueGoodTable>
    </div>
</template>

<script>
import { groupBy, orderBy } from 'lodash';
import { VueGoodTable } from 'vue-good-table';
import 'vue-good-table/dist/vue-good-table.css'

export default {
    components: {
        VueGoodTable,
    },

    props: {
        contacts: { default: () => [] },
    },

    data() {
        return {
            columns: [
                {
                    field: 'property',
                    label: this.$t('Property'),
                    thClass: 'first-column',
                },
                {
                    field: 'mls_number',
                    label: this.$t('MLS #'),
                },
                {
                    field: 'type',
                    label: this.$t('Role'),
                },
                {
                    field: 'id',
                    label: this.$t('ID'),
                    hidden: true,
                    globalSearchDisabled: true,
                },
                {
                    field: 'broker_name',
                    label: this.$t('Broker Name'),
                    hidden: true,
                },
                {
                    field: 'broker_email',
                    label: this.$t('Broker Email'),
                    hidden: true,
                },
            ],
        }
    },

    computed: {
        contacts_grouped() {
            return orderBy(Object.values(groupBy(this.contacts, 'name')).map(contact => {
                return {
                    name: contact[0].name,
                    email: contact.find(contact => contact.email)?.email,
                    address: this.formatAddress(contact.find(contact => contact.address_1)),
                    phone: contact.find(contact => contact.phone)?.phone,
                    deals: contact,
                }
            }), 'name', 'asc');
        },
        rows() {
            return this.contacts_grouped.map(contact => {
                return {
                    label: {
                        name: contact.name,
                        email: contact.email,
                        address: contact.address,
                        phone: contact.phone,
                        deal_count: contact.deals.length,
                    },
                    mode: 'span',
                    children: contact.deals.map(contact => {
                        return {
                            type: this.formatType(contact.type),
                            property: this.formatAddress(contact.deal.property),
                            mls_number: contact.deal.property.mls_number,
                            id: contact.deal.property.id,
                            broker_name: contact.name,
                            broker_email: contact.email,
                        }
                    }),
                }
            });
        },
    },

    methods: {
        clickRow({ row }) {
            window.location.href = '/properties/' + row.id;
        },
        formatAddress(object) {
            if (object) {
                return [
                    object.address || object.address_1,
                    object.address_2,
                    object.city,
                    [object.state, object.zip].join(' '),
                ].filter(value => value && value.trim())
                .join(', ');
            }
        },
        formatType(type) {
            return {
                'buyer_1': 'Buyer',
                'buyer_2': 'Buyer',
                'seller_1': 'Seller',
                'seller_2': 'Seller',
            }[type] || this.titleCase(type);
        },
        titleCase(str) {
            return str.replace (/^[-_]*(.)/, (_, c) => c.toUpperCase())
                .replace (/[-_]+(.)/g, (_, c) => ' ' + c.toUpperCase());
        },
    },
}
</script>

<style>
.first-column > span {
    @apply pl-8;
}

.vgt-table thead th {
    @apply font-normal text-sm;
}

.vgt-global-search {
    padding: 10px 0;
}

.vgt-global-search__input input {
    font-size: 18px;
    height: 48px;
}

.vgt-global-search__input .input__icon {
    left: 3px;
    top: 10px;
    opacity: .6;
}

.vgt-global-search__input .input__icon .magnifying-glass {
    height: 14px;
    width: 14px;
}

.vgt-table th.vgt-row-header {
    background-color: #e1eff8;
    padding-left: 10px;
}

.vgt-table th.vgt-row-header span {
    display: flex;
    align-items: center;
}

.vgt-table th.vgt-row-header span.triangle {
    margin: 0 2px;
}

table.vgt-table tr.clickable:hover {
    background-color: #f4f8fb;
}

.vgt-table.bordered th.vgt-row-header {
    border-bottom: none;
    border-top: 2px solid #6ba4c7;
}
</style>
